import type { KnopkaGlassPerformance } from "./knopkaGlassTokens";

const PERF_CLASSES = ["knopka-glass-perf-high", "knopka-glass-perf-balanced", "knopka-glass-perf-low"] as const;

export type ResolvedGlassPerformance = "high" | "balanced" | "low";

function supportsBackdropFilter(): boolean {
  if (typeof CSS === "undefined" || !CSS.supports) return true;
  return CSS.supports("backdrop-filter", "blur(1px)") || CSS.supports("-webkit-backdrop-filter", "blur(1px)");
}

export function resolveGlassPerformance(mode: KnopkaGlassPerformance): ResolvedGlassPerformance {
  if (mode === "high") return "high";
  if (mode === "balanced") return "balanced";
  if (mode === "low") return "low";

  if (typeof window === "undefined") return "balanced";

  if (!supportsBackdropFilter()) return "low";

  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const reducedTransparency = window.matchMedia?.("(prefers-reduced-transparency: reduce)")?.matches;
  if (reducedMotion || reducedTransparency) return "low";

  const cores = typeof navigator.hardwareConcurrency === "number" ? navigator.hardwareConcurrency : 8;
  const memory =
    typeof (navigator as Navigator & { deviceMemory?: number }).deviceMemory === "number"
      ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory!
      : 8;
  const coarsePointer = window.matchMedia?.("(hover: none) and (pointer: coarse)")?.matches;

  if (cores <= 4 || memory <= 4) return coarsePointer ? "low" : "balanced";
  if (coarsePointer && memory <= 6) return "balanced";
  return "high";
}

export function applyGlassPerformanceClass(
  root: HTMLElement,
  mode: KnopkaGlassPerformance,
  active: boolean
): ResolvedGlassPerformance | null {
  for (const className of PERF_CLASSES) {
    root.classList.remove(className);
  }
  if (!active) return null;
  const resolved = resolveGlassPerformance(mode);
  root.classList.add(`knopka-glass-perf-${resolved}`);
  return resolved;
}

export function clearGlassPerformanceClass(root: HTMLElement): void {
  for (const className of PERF_CLASSES) {
    root.classList.remove(className);
  }
}
